import React, { useRef, useEffect, useState } from "react";
import { API } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkedUpload";
import { toast } from "sonner";
import { Bold, Italic, Underline, Baseline, ImagePlus, Paperclip, X, FileText, Loader2, AlignLeft, AlignCenter, AlignRight } from "lucide-react";

const COLORS = ["#111111", "#dc2626", "#ea580c", "#ca8a04", "#16a34a", "#0891b2", "#2563eb", "#7c3aed", "#db2777", "#6b7280"];

const escapeHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const unescapeHtml = (s) => {
  const el = document.createElement("textarea");
  el.innerHTML = s || "";
  return el.value;
};

// Convierte texto plano (pegado desde Word/PDF) en HTML limpio preservando los saltos de párrafo.
const plainToHtml = (text) => {
  const lines = (text || "").replace(/\r\n|\r/g, "\n").split("\n");
  const html = lines
    .map((l) => (l.trim() ? `<div>${escapeHtml(l)}</div>` : "<div><br/></div>"))
    .join("");
  return html || "<div><br/></div>";
};

const DIA_RE = /(?=(?:LUNES|MARTES|MI[ÉE]RCOLES|JUEVES|VIERNES|S[ÁA]BADO|DOMINGO)\b)/gi;
const DIA_COUNT = /LUNES|MARTES|MI[ÉE]RCOLES|JUEVES|VIERNES|S[ÁA]BADO|DOMINGO/gi;
const DIA_START = /^(?:LUNES|MARTES|MI[ÉE]RCOLES|JUEVES|VIERNES|S[ÁA]BADO|DOMINGO)\b/i;

// ¿El contenido está "de corrido"? (2+ días y alguno NO al inicio de su bloque).
const isRunon = (v) => {
  const blocks = v.split(/<\/(?:div|p)>|<br\s*\/?>/i);
  let total = 0, misplaced = 0;
  blocks.forEach((b) => {
    const txt = b.replace(/<[^>]+>/g, "").trim();
    const m = txt.match(DIA_COUNT);
    if (!m) return;
    total += m.length;
    if (m.length > 1) misplaced += m.length - 1;
    if (!DIA_START.test(txt)) misplaced += 1;
  });
  return total >= 2 && misplaced > 0;
};

// Reflow agresivo: reconstruye el texto visible y lo separa por días y 'Actividad N',
// sin importar cómo estaba guardado el marcado. Preserva contenido con imágenes.
const normalizeIncoming = (v) => {
  if (!v || typeof v !== "string") return v;
  if (/<img/i.test(v)) return v; // no tocar si hay imágenes incrustadas
  if (!isRunon(v)) return v; // ya está separado o no aplica
  let t = v.replace(/\r\n|\r/g, "\n")
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\s*\/\s*(?:div|p|li|tr|h[1-6])\s*>/gi, "\n")
    .replace(/<\s*(?:div|p|li|tr|h[1-6])[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "");
  t = unescapeHtml(t)
    .split("\n").map((ln) => ln.replace(/[ \t]+/g, " ").trim()).join("\n")
    .replace(/\n+/g, " ").trim();
  if (t.length < 120) return v;
  let r = t.replace(DIA_RE, "\n\n").replace(/\s*(?=(?:ACTIVIDAD|Actividad)\s*\d)/g, "\n");
  r = r.replace(/^\s+/, "").replace(/\n{3,}/g, "\n\n");
  const parts = [];
  r.split("\n\n").forEach((chunk) => {
    const lines = chunk.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    parts.push(`<div>${lines.map(escapeHtml).join("<br/>")}</div>`);
    parts.push("<div><br/></div>");
  });
  if (parts[parts.length - 1] === "<div><br/></div>") parts.pop();
  const out = parts.join("");
  return out || v;
};

// Editor de texto enriquecido tipo "Word" (drop-in de AttachTextarea).
// Emite onChange({ target: { value: html } }) para ser compatible con los setters existentes.
export default function RichText({
  value, onChange, placeholder, testid, rows = 2, minRows, color = "#111",
  files = [], onFilesChange, label = "", variant = "bordered",
}) {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showColors, setShowColors] = useState(false);
  const mr = minRows || rows || 2;

  // Contenido inicial (no controlado para no perder el cursor al escribir).
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el) {
      const norm = normalizeIncoming(value || "");
      if (el.innerHTML !== norm) el.innerHTML = norm;
    }
  }, [value]);

  const emit = () => { if (ref.current && onChange) onChange({ target: { value: ref.current.innerHTML } }); };
  const focusEd = () => { if (ref.current) ref.current.focus(); };
  const exec = (cmd, val = null) => { try { document.execCommand("styleWithCSS", false, false); } catch {} focusEd(); document.execCommand(cmd, false, val); emit(); };
  const applyColor = (c) => { focusEd(); try { document.execCommand("styleWithCSS", false, false); } catch {} document.execCommand("foreColor", false, c); setShowColors(false); emit(); };
  // Alineación: usa CSS (text-align) para que el PDF pueda respetarla.
  const align = (dir) => { focusEd(); try { document.execCommand("styleWithCSS", false, true); } catch {} document.execCommand(dir === "center" ? "justifyCenter" : dir === "right" ? "justifyRight" : "justifyLeft"); emit(); };

  // Pegado inteligente: usa texto plano y conserva los saltos de párrafo (evita el marcado sucio de Word/PDF).
  const onPaste = (e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    e.preventDefault();
    const text = cd.getData("text/plain");
    if (!text) return;
    focusEd();
    document.execCommand("insertHTML", false, plainToHtml(text));
    emit();
  };

  // Enter siempre crea un salto de párrafo real y consistente.
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      document.execCommand("insertHTML", false, "<div><br/></div>");
      emit();
    }
  };

  const insertImage = async (fl) => {
    const f = fl && fl[0]; if (!f) return;
    setUploading(true);
    try {
      setProgress(0);
      const data = await uploadFileChunked(f, { onProgress: setProgress });
      focusEd();
      const url = `${API}/aula/archivo/${data.token}`;
      document.execCommand("insertHTML", false,
        `<img src="${url}" data-token="${data.token}" alt="${(data.name || "").replace(/"/g, "")}" style="max-width:100%;height:auto;border-radius:6px;margin:4px 0;" /><br/>`);
      emit();
      toast.success("Imagen insertada");
    } catch { toast.error("No se pudo insertar la imagen"); }
    finally { setUploading(false); setProgress(0); if (imgRef.current) imgRef.current.value = ""; }
  };

  const attach = async (fl) => {
    if (!fl?.length || !onFilesChange) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of fl) { setProgress(0); const data = await uploadFileChunked(f, { onProgress: setProgress }); added.push({ token: data.token, name: data.name, mime: data.mime, label: label || "" }); }
      onFilesChange([...(files || []), ...added]);
      toast.success("Archivo adjuntado");
    } catch { toast.error("No se pudo adjuntar el archivo"); }
    finally { setUploading(false); setProgress(0); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeAt = (i) => onFilesChange((files || []).filter((_, j) => j !== i));

  const TBtn = ({ onClick, title, active, children, tid }) => (
    <button type="button" title={title} data-testid={tid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="w-7 h-7 inline-flex items-center justify-center rounded text-nardo-600 hover:bg-nardo-100 hover:text-navy transition-colors">{children}</button>
  );

  return (
    <div>
      <div className="flex items-center gap-0.5 flex-wrap border border-nardo-200 border-b-0 rounded-t-lg bg-nardo-50/70 px-1 py-0.5" data-testid={testid ? `${testid}-toolbar` : undefined}>
        <TBtn title="Negrita" onClick={() => exec("bold")} tid={testid ? `${testid}-bold` : undefined}><Bold className="w-3.5 h-3.5" /></TBtn>
        <TBtn title="Cursiva" onClick={() => exec("italic")} tid={testid ? `${testid}-italic` : undefined}><Italic className="w-3.5 h-3.5" /></TBtn>
        <TBtn title="Subrayado" onClick={() => exec("underline")} tid={testid ? `${testid}-underline` : undefined}><Underline className="w-3.5 h-3.5" /></TBtn>
        <div className="relative">
          <TBtn title="Color de texto" onClick={() => setShowColors((s) => !s)} tid={testid ? `${testid}-color` : undefined}><Baseline className="w-3.5 h-3.5" /></TBtn>
          {showColors && (
            <div className="absolute z-[90] top-9 left-0 bg-white border border-nardo-200 rounded-lg shadow-lg p-2 grid grid-cols-5 gap-2" data-testid={testid ? `${testid}-color-palette` : undefined}>
              {COLORS.map((c) => (
                <button key={c} type="button" title={c} onMouseDown={(e) => e.preventDefault()} onClick={() => applyColor(c)}
                  className="w-5 h-5 rounded-full border border-nardo-200 hover:scale-110 transition-transform" style={{ background: c }} data-testid={testid ? `${testid}-color-${c.replace('#', '')}` : undefined} />
              ))}
            </div>
          )}
        </div>
        <span className="w-px h-5 bg-nardo-200 mx-0.5" />
        <TBtn title="Alinear a la izquierda" onClick={() => align("left")} tid={testid ? `${testid}-align-left` : undefined}><AlignLeft className="w-3.5 h-3.5" /></TBtn>
        <TBtn title="Centrar" onClick={() => align("center")} tid={testid ? `${testid}-align-center` : undefined}><AlignCenter className="w-3.5 h-3.5" /></TBtn>
        <TBtn title="Alinear a la derecha" onClick={() => align("right")} tid={testid ? `${testid}-align-right` : undefined}><AlignRight className="w-3.5 h-3.5" /></TBtn>
        <span className="w-px h-5 bg-nardo-200 mx-0.5" />
        <label title="Insertar imagen en el texto" className="w-7 h-7 inline-flex items-center justify-center rounded text-nardo-600 hover:bg-nardo-100 hover:text-navy cursor-pointer transition-colors" data-testid={testid ? `${testid}-insert-image` : undefined}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => insertImage(Array.from(e.target.files || []))} />
        </label>
        {onFilesChange && (
          <label title="Adjuntar archivo (PDF, video…)" className="w-7 h-7 inline-flex items-center justify-center rounded text-nardo-600 hover:bg-nardo-100 hover:text-navy cursor-pointer transition-colors" data-testid={testid ? `${testid}-attach` : undefined}>
            <Paperclip className="w-3.5 h-3.5" />
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => attach(Array.from(e.target.files || []))} />
          </label>
        )}
        {uploading && <span className="text-[10px] text-nardo-500 ml-1">Subiendo… {progress}%</span>}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        data-testid={testid}
        data-placeholder={placeholder || "Escribe aquí…"}
        className="rt-editor w-full border border-nardo-200 rounded-b-lg px-3 py-2 text-sm outline-none focus:border-navy overflow-auto"
        style={{ minHeight: mr * 26, color: "#111", "--tw-ring-color": color }}
      />
      {onFilesChange && (files || []).length > 0 && (
        <div className="flex flex-wrap items-start gap-2 mt-1.5">
          {(files || []).map((f, i) => {
            const isImg = (f.mime || "").startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(f.name || "");
            const url = `${API}/aula/archivo/${f.token}`;
            return isImg ? (
              <div key={i} className="relative" data-testid={testid ? `${testid}-file-${i}` : undefined}>
                <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.name} loading="lazy" className="h-16 w-16 object-cover rounded-md border border-nardo-200" /></a>
                <button type="button" onClick={() => removeAt(i)} className="absolute -top-1.5 -right-1.5 bg-white border border-nardo-200 rounded-full p-0.5 text-nardo-400 hover:text-red-600 shadow-sm"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <span key={i} className="text-[10px] inline-flex items-center gap-1 bg-nardo-100 text-navy rounded-md px-2 py-1" data-testid={testid ? `${testid}-file-${i}` : undefined}>
                <a href={url} target="_blank" rel="noreferrer" className="hover:underline max-w-[8rem] truncate inline-flex items-center gap-1"><FileText className="w-3 h-3 shrink-0" />{f.name}</a>
                <button type="button" onClick={() => removeAt(i)} className="text-nardo-400 hover:text-red-600"><X className="w-3 h-3" /></button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
